import { GRID_SECTION_HANDLE, HEADER_SECTION_HANDLE, HISTORY_SKIP_PARAMS } from "./constants";
import { fetchSectionHtml, getGridSection, getHeaderSection } from "./dom";
import { hideGridLoadingState, patchGridFromHtml, showGridLoadingState } from "./patch";
import { patchHeaderFromHtml } from "./patchHeader";
import { pushCollectionState, initCollectionHistory, type CollectionHistoryState } from "./history";
import { runAfterCollectionUpdate, type CollectionUpdateMode } from "./postUpdate";
import { buildDisplayUrl, buildSectionUrl, collectionHandleFromPath } from "./urls";

export type CollectionUpdateRequest = {
  pathname: string;
  search: string;
  navParent: string | null;
  mode: CollectionUpdateMode;
  pushHistory?: boolean;
  preserveSubcollectionNav?: boolean;
  showSkeleton?: boolean;
};

const COUNT_SECTION_HANDLE = "products-collection-count";

let updateInFlight = false;

const SKELETON_ITEM =
  '<div class="animate-pulse flex flex-col gap-2"><div class="bg-gray-100 rounded-xl w-full aspect-[3/4]"></div><div class="bg-gray-100 rounded h-3 w-3/4"></div><div class="bg-gray-100 rounded h-3 w-1/2"></div></div>';

const normalizeSearch = (search: string) => {
  if (!search || search === "?") return "";
  return search.startsWith("?") ? search : `?${search}`;
};

const showGridSkeleton = (gridSection: HTMLElement) => {
  const grid = gridSection.querySelector<HTMLElement>("#products-grid");
  const loading = gridSection.querySelector<HTMLElement>("#loading-indicator");
  if (!grid) return;
  grid.dataset.skeleton = "true";
  grid.innerHTML = SKELETON_ITEM.repeat(12);
  loading?.classList.remove("hidden");
};

const hideGridSkeleton = (gridSection: HTMLElement) => {
  gridSection.querySelector<HTMLElement>("#loading-indicator")?.classList.add("hidden");
  const grid = gridSection.querySelector<HTMLElement>("#products-grid");
  if (!grid) return;
  grid.style.opacity = "1";
  grid.removeAttribute("aria-busy");
};

export const fetchCollectionCount = async (pathname: string, search: string, navParent: string | null) => {
  const rawUrl = pathname + normalizeSearch(search);
  const fetchUrl = buildSectionUrl(rawUrl, COUNT_SECTION_HANDLE, navParent);
  const html = await fetchSectionHtml(fetchUrl);
  if (!html) return null;

  const doc = new DOMParser().parseFromString(html, "text/html");
  return doc.querySelector("#collection-count")?.textContent?.trim() ?? null;
};

export const applyCollectionUpdate = async (request: CollectionUpdateRequest): Promise<boolean> => {
  if (updateInFlight) return false;

  const headerSection = getHeaderSection();
  const gridSection = getGridSection();
  if (!gridSection) return false;

  updateInFlight = true;

  const pathUrl = new URL(request.pathname + normalizeSearch(request.search), window.location.origin);
  const displayUrl = buildDisplayUrl(pathUrl, request.navParent, HISTORY_SKIP_PARAMS);
  const displayPath = displayUrl.pathname + displayUrl.search;
  const rawFetchUrl = pathUrl.pathname + pathUrl.search;
  const activeHandle = collectionHandleFromPath(pathUrl.pathname);

  const loading = gridSection.querySelector<HTMLElement>("#loading-indicator");
  const gridContainer = gridSection.querySelector<HTMLElement>("#products-grid");

  if (request.showSkeleton && headerSection) {
    showGridSkeleton(gridSection);
    gridSection.setAttribute("aria-busy", "true");
  } else if (gridContainer && loading) {
    showGridLoadingState(gridContainer, loading);
  }

  try {
    if (request.mode === "nav") {
      if (!headerSection) return false;

      const preserveNav = request.preserveSubcollectionNav ?? false;
      const headerFetch = buildSectionUrl(rawFetchUrl, HEADER_SECTION_HANDLE, request.navParent);
      const gridFetch = buildSectionUrl(rawFetchUrl, GRID_SECTION_HANDLE, request.navParent);

      const [headerHtml, gridHtml] = await Promise.all([fetchSectionHtml(headerFetch), fetchSectionHtml(gridFetch)]);

      if (!headerHtml || !gridHtml) {
        window.location.assign(displayPath);
        return false;
      }

      patchHeaderFromHtml(headerSection, headerHtml, preserveNav);
      patchGridFromHtml(gridSection, gridHtml);
      hideGridSkeleton(gridSection);

      if (request.pushHistory !== false) {
        pushCollectionState(displayPath, request.navParent);
      }

      await runAfterCollectionUpdate({
        mode: "nav",
        scope: document,
        navParent: request.navParent,
        preserveSubcollectionNav: preserveNav,
        headerSection,
        activeHandle,
      });

      return true;
    }

    const gridFetch = buildSectionUrl(rawFetchUrl, GRID_SECTION_HANDLE, request.navParent);
    const gridHtml = await fetchSectionHtml(gridFetch);

    if (!gridHtml) {
      window.location.assign(displayPath);
      return false;
    }

    patchGridFromHtml(gridSection, gridHtml);

    if (gridContainer && loading) {
      hideGridLoadingState(gridContainer, loading);
    }

    if (request.pushHistory !== false) {
      pushCollectionState(displayPath, request.navParent);
    }

    await runAfterCollectionUpdate({
      mode: "filter",
      scope: gridSection,
      navParent: request.navParent,
    });

    return true;
  } finally {
    gridSection.removeAttribute("aria-busy");
    updateInFlight = false;
  }
};

export const initCollectionPageRouter = (onPopState: (state: CollectionHistoryState) => void) => {
  initCollectionHistory(onPopState);
};

export const isCollectionUpdateInFlight = () => updateInFlight;
